import clsx from "clsx";
import React from "react";
import { FeatureArticleRichText } from "web/react/components/feature-article/feature-article-rich-text";
import { withHardBreaks } from "web/react/components/feature-article/utils";
import { Heading } from "web/react/emo/heading";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import * as styles from "./feature-article-table.css";
import { useEqualRowHeight } from "./use-equal-row-height";

interface FeatureArticleTableProps {
    title: string | null;
    subtitle: string | null;
    columnHeadings: string[];
    rows: string[][];
    body: string | null;
}

export function FeatureArticleTable({
    title,
    subtitle,
    columnHeadings,
    rows,
    body,
}: FeatureArticleTableProps): React.ReactElement {
    const { isDesktopViewport, isTabletViewport } = useDomViewport();
    const tbodyRef = useEqualRowHeight(isDesktopViewport || isTabletViewport);

    return (
        <VStack spacing="md">
            {(title || subtitle) && (
                <VStack spacing="sm">
                    {title && (
                        <Heading as="h2" textStyle="hero">
                            {title}
                        </Heading>
                    )}
                    {subtitle && (
                        <Text textStyle="body" color="secondary">
                            {subtitle}
                        </Text>
                    )}
                </VStack>
            )}

            <div className={styles.scrollContainer}>
                <table className={styles.table}>
                    <thead className={styles.visuallyHidden}>
                        <tr className={styles.headerRow}>
                            {columnHeadings.map((heading, index) => (
                                <th key={index} scope="col" className={styles.headerCell}>
                                    <Text as="span" textStyle="caption-s" upperCase>
                                        {heading}
                                    </Text>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody ref={tbodyRef} className={styles.tableBody}>
                        {rows.map((row, rowIndex) => (
                            <tr key={rowIndex} className={styles.row}>
                                {row.map((cell, cellIndex) => (
                                    <td
                                        key={cellIndex}
                                        className={clsx(
                                            styles.cell,
                                            cellIndex === 0 && styles.rowHeaderCell
                                        )}
                                    >
                                        {cellIndex > 0 && (
                                            <span
                                                aria-hidden="true"
                                                className={styles.mobileFieldLabel}
                                            >
                                                {columnHeadings[cellIndex]}
                                            </span>
                                        )}
                                        <FeatureArticleRichText
                                            textStyle="body-s"
                                            className={styles.rowText}
                                        >
                                            {withHardBreaks(cell)}
                                        </FeatureArticleRichText>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {body && <FeatureArticleRichText>{withHardBreaks(body)}</FeatureArticleRichText>}
        </VStack>
    );
}
